import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { shiftDay, type DayRange } from "@/lib/zoned-day";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

// Food spend and covers are stored against `date` columns, so this whole hook works in the
// site's calendar days (`useWasteFilters.dayRange`) — never in instants, which a viewer in
// another zone would recover a day off (POS2-1039).

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type FoodSpendPeriod = {
  id: string;
  org_id: string;
  site_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
  /** Who last entered this period (updated_by ?? created_by), resolved to a display
   *  name for the provenance chip (POS2-969); null for legacy rows with no writer. */
  entered_by_id: string | null;
  entered_by_name: string | null;
};

/**
 * Provenance-chip label for who entered the latest period (POS2-969): "you" when the
 * viewer is the entrant, their display name when someone else, null when unknown (no
 * entrant id, or an id that resolved to no name) → the chip then shows the date only.
 * Structural on purpose so it also serves `CoversPeriod`.
 */
export function entrantLabel(
  period: { entered_by_id: string | null; entered_by_name: string | null } | null,
  currentUserId: string | null,
): string | null {
  if (!period?.entered_by_id) return null;
  if (period.entered_by_id === currentUserId) return "you";
  return period.entered_by_name;
}

/** Sum of period amounts, or null when there are none (drives the KPI's "N/A"). */
export function sumFoodSpend(periods: FoodSpendPeriod[] | undefined): number | null {
  if (!periods || periods.length === 0) return null;
  return periods.reduce((total, p) => total + p.amount, 0);
}

/** Inclusive day count between two yyyy-MM-dd dates; 0 when end precedes start. */
function inclusiveDays(start: string, end: string): number {
  return Math.max(0, differenceInCalendarDays(parseISO(end), parseISO(start)) + 1);
}

/**
 * A range total and how much of it the app derived rather than read (POS2-996).
 * `filledDays > 0` is what makes the figure an estimate, and what the KPI card's
 * "Est. · N of M days filled" chip reports.
 */
export type RangeEstimate = {
  total: number;
  /** Range days an entered period covers. */
  enteredDays: number;
  /** Gap days between entered weeks, filled at the daily average. */
  filledDays: number;
  /** Days in the range — the chip's denominator. Unentered, unfilled days are the rest. */
  rangeDays: number;
};

/**
 * Food spend attributable to the selected range — the Waste % denominator (POS2-923),
 * and the Food spend card's own figure once days go unentered (POS2-996). Two rules:
 *
 * 1. Days covered by an entered period count at that period's per-day rate
 *    (even-daily-spend approximation; summed where entries overlap) — so a
 *    sub-period selection divides by its share of the week, not the full week.
 * 2. Days *between* entered weeks are filled at the **daily average across the entered
 *    weeks** — total entered amount ÷ the distinct days those entries cover, so a week
 *    that went unentered doesn't deflate the %.
 *
 * **Gaps only** (product decision): a range day outside the entered span contributes
 * nothing. Interpolating between two known weeks is a claim the entries support;
 * projecting a rate onto days beyond every entry is not, so a range reaching past the
 * last entered week reads low rather than inventing spend.
 *
 * A week still in progress is left out of the average basis: its number is a whole-week
 * figure spread over a span only partly elapsed, so letting it into the divisor would
 * push POS2-944's distortion into every filled day. Its own days still count as entered.
 * When nothing has fully elapsed it is the basis — the alternative is no estimate at all.
 *
 * Days are the site's calendar days, matching how periods are entered against the
 * viewed frame: an open-ended `to` counts to today; an unbounded `from` (all-time)
 * has no enumerable days to fill, so every period counts in full. Null when there
 * are no periods (KPI shows N/A).
 */
export function estimateRangeSpend(
  periods: FoodSpendPeriod[] | undefined,
  range: DayRange,
  today: string,
): RangeEstimate | null {
  if (!periods || periods.length === 0) return null;
  const rangeStart = range.from;
  const rangeEnd = range.to ?? today;

  // ≥ 1 day so a degenerate stored range still yields a per-day figure instead
  // of dropping the period (yyyy-MM-dd strings compare lexically).
  const entries = periods.map((p) => ({
    start: p.period_start,
    end: p.period_end,
    amount: p.amount,
    rate: p.amount / Math.max(1, inclusiveDays(p.period_start, p.period_end)),
  }));

  if (rangeStart == null) {
    return entries.reduce<RangeEstimate>(
      (acc, e) => {
        const end = rangeEnd < e.end ? rangeEnd : e.end;
        const days = inclusiveDays(e.start, end);
        return {
          total: acc.total + e.rate * days,
          enteredDays: acc.enteredDays + days,
          filledDays: 0,
          rangeDays: acc.rangeDays + days,
        };
      },
      { total: 0, enteredDays: 0, filledDays: 0, rangeDays: 0 },
    );
  }

  // Weighted over entered days, counting a day once even when two invoices land on the
  // same week — a day-count sum would halve that week's rate.
  const elapsed = entries.filter((e) => e.end < today);
  const basis = elapsed.length ? elapsed : entries;
  const basisDays = new Set<string>();
  for (const e of basis) {
    for (let day = e.start; day <= e.end; day = shiftDay(day, 1)) basisDays.add(day);
  }
  const dailyAverage = basis.reduce((sum, e) => sum + e.amount, 0) / Math.max(1, basisDays.size);

  // Per-day entered rate across the range; null marks a day no period covers.
  const dayRates: Array<number | null> = [];
  for (let day = rangeStart; day <= rangeEnd; day = shiftDay(day, 1)) {
    const covering = entries.filter((e) => e.start <= day && day <= e.end);
    dayRates.push(covering.length ? covering.reduce((sum, e) => sum + e.rate, 0) : null);
  }
  // The entered span: only null days inside it are gaps to fill.
  let firstEntered = -1;
  let lastEntered = -1;
  dayRates.forEach((rate, i) => {
    if (rate == null) return;
    if (firstEntered === -1) firstEntered = i;
    lastEntered = i;
  });

  let total = 0;
  let enteredDays = 0;
  let filledDays = 0;
  dayRates.forEach((rate, i) => {
    if (rate != null) {
      total += rate;
      enteredDays += 1;
      return;
    }
    if (i > firstEntered && i < lastEntered) {
      total += dailyAverage;
      filledDays += 1;
    }
  });
  return { total, enteredDays, filledDays, rangeDays: dayRates.length };
}

/**
 * Build the food-spend list query params from a site + day range (POS2-942).
 *
 * `food_spend_periods.period_*` are `date` columns, so both bounds are calendar days —
 * the site's, since POS2-1039. The upper bound caps an open-ended "from X onward" preset
 * (this_week/today/7d/…) at **today** so a future-dated period can't leak into the view —
 * without this the overlap keeps any period ending on/after `from`. A fully unbounded
 * range (no `from` — e.g. the dialog's all-periods list) stays unbounded.
 */
export function buildFoodSpendParams(
  siteId: string | null,
  range: DayRange,
  today: string,
): URLSearchParams {
  const params = new URLSearchParams();
  if (siteId) params.set("site_id", siteId);
  if (range.from) params.set("date_from", range.from);
  const toBound = range.to ?? (range.from ? today : null);
  if (toBound) params.set("date_to", toBound);
  return params;
}

/**
 * Food spend periods for the active site overlapping the dashboard's day range.
 * Runs whenever authenticated — an unbounded range lists all of the org's periods
 * (site-scoped server-side). No `placeholderData`: results are scope-specific.
 */
export function useFoodSpendPeriods(siteId: string | null, range: DayRange, today: string) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = buildFoodSpendParams(siteId, range, today);

  return useQuery<FoodSpendPeriod[]>({
    queryKey: ["food-spend", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/food-spend?${params.toString()}`, { headers });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: FoodSpendPeriod[] };
      return body.data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });
}

export type FoodSpendCreateInput = {
  site_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  currency: string;
};

export function useCreateFoodSpend() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: FoodSpendCreateInput): Promise<FoodSpendPeriod> => {
      const res = await fetch(`${API_URL}/api/v1/food-spend`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: FoodSpendPeriod }).data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["food-spend"] });
    },
  });
}

export type FoodSpendUpdateInput = {
  id: string;
  site_id?: string;
  period_start?: string;
  period_end?: string;
  amount?: number;
  currency?: string;
};

export function useUpdateFoodSpend() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }: FoodSpendUpdateInput): Promise<FoodSpendPeriod> => {
      const res = await fetch(`${API_URL}/api/v1/food-spend/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: FoodSpendPeriod }).data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["food-spend"] });
    },
  });
}

export function useDeleteFoodSpend() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/api/v1/food-spend/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["food-spend"] });
    },
  });
}
